import React, { useState, useEffect, useRef } from 'react';
import apiService from '../services/api';
import { authFetch } from '../services/api';
import { Application, APRModel, Framework } from '../types';
import { useAPR } from '../context/APRContext';
import { useAuth } from '../context/AuthContext';

interface BulkModelRow {
  name: string;
  frameworkId: string;
  applicationCodes: string[];
  description: string;
  matchedAppIds: string[];
  unmatchedCodes: string[];
}


const APRModelPage: React.FC = () => {
  const { state, refreshData } = useAPR();
  const { currentUser } = useAuth();
  const models = state.models;
  const applications = state.applications;
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingModel, setEditingModel] = useState<APRModel | null>(null);
  const [availableApplications, setAvailableApplications] = useState<Application[]>([]);
  const [allApplications, setAllApplications] = useState<Application[]>([]);
  const [assignedApplicationIds, setAssignedApplicationIds] = useState<Set<string>>(new Set());
  const [activeFrameworks, setActiveFrameworks] = useState<Framework[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [modelSearch, setModelSearch] = useState('');
  const [appSearch, setAppSearch] = useState('');
  const [appSearchModelIds, setAppSearchModelIds] = useState<Set<string> | null>(null);
  const [appSearchLoading, setAppSearchLoading] = useState(false);
  const [gridScale, setGridScale] = useState(320); // Grid card min-width
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    frameworkId: ''
  });
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
  const [applicationFilter, setApplicationFilter] = useState('');
  const [selectedAppFilter, setSelectedAppFilter] = useState('');
  const [availableAppFilter, setAvailableAppFilter] = useState('');

  // Bulk upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showBulkPreview, setShowBulkPreview] = useState(false);
  const [bulkRows, setBulkRows] = useState<BulkModelRow[]>([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [showImportLog, setShowImportLog] = useState(false);
  const [importLog, setImportLog] = useState<{ created: number; failed: number; unmatchedByModel: { model: string; codes: string[] }[]; errors: string[] }>({ created: 0, failed: 0, unmatchedByModel: [], errors: [] });

  // Unassigned applications state
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [unassignedApps, setUnassignedApps] = useState<Application[]>([]);
  const [selectedUnassignedApps, setSelectedUnassignedApps] = useState<Set<string>>(new Set());
  const [assignToModelId, setAssignToModelId] = useState<string>('');
  const [unassignedFilter, setUnassignedFilter] = useState('');
  const [assigningApps, setAssigningApps] = useState(false);

  // Delete all state
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (models.length > 0 && applications.length > 0) {
      loadData();
    }
  }, [models, applications]);

  const loadAssignedApplicationIds = async (_modelsData?: APRModel[]) => {
    const ids = await apiService.getAssignedApplicationIds();
    const assignedIds = new Set<string>(ids);
    setAssignedApplicationIds(assignedIds);
    return assignedIds;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Use context data instead of making redundant API calls
      setAvailableApplications(applications);
      setAllApplications(applications);
      
      // Load assigned application IDs
      await loadAssignedApplicationIds(models);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let modelId: string;
      
      if (editingModel) {
        // Preserve the original status when updating a model
        const updateData = {
          ...formData,
          status: editingModel.status
        };
        await apiService.updateModel(editingModel.id, updateData);
        modelId = editingModel.id;
        alert('APR Model Definition updated successfully!');
      } else {
        const newModel = await apiService.createModel(formData);
        modelId = newModel.id;
        alert('APR Model Definition created successfully!');
      }
      
      // Update application selections
      await apiService.updateModelApplications(modelId, selectedApplications);
      
      resetForm();
      await refreshData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message;
      alert(`Error ${editingModel ? 'updating' : 'creating'} APR Model Definition: ${errorMessage}`);
    }
  };

  const resetForm = async () => {
    setFormData({
      name: '',
      description: '',
      frameworkId: ''
    });
    setSelectedApplications([]);
    setApplicationFilter('');
    setSelectedAppFilter('');
    setAvailableAppFilter('');
    setShowForm(false);
    setEditingModel(null);
    setIsViewOnly(false);
    // Reload all applications when closing the form
    await loadData();
  };

  // ========== Bulk Upload Functions ==========

  const downloadTemplate = () => {
    const csvContent =
      'Model Name,Framework ID,Selected Applications,Description\n' +
      '"Energy / Common Services / Digital Workplace & Reporting / Blue","ITSF","IT.059; IT.066; IT.096","Energy - Blue rationalization group for L1: Common Services / L2: Digital Workplace & Reporting."\n' +
      '"Energy / Hire to Retire / Human Resources / Red","BPF","IT.299","Energy - Red rationalization group for L1: Hire to Retire / L2: Human Resources."';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'apr_model_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';

    const text = await file.text();
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) {
      alert('CSV file is empty or has no data rows.');
      return;
    }

    // Build application code → ID lookup from all applications
    const allApps = await apiService.getApplications();
    const codeToId = new Map<string, string>();
    allApps.forEach((app: Application) => {
      if (app.applicationIdCode) {
        codeToId.set(app.applicationIdCode.trim().toUpperCase(), app.id);
      }
    });

    // Load frameworks for ID mapping (by id, name, code, and word tokens)
    const frameworks = await apiService.getActiveFrameworks();
    const fwLookup = new Map<string, string>();
    frameworks.forEach((fw: any) => {
      fwLookup.set(fw.id.toUpperCase(), fw.id);
      fwLookup.set(fw.name.toUpperCase(), fw.id);
      if (fw.code) fwLookup.set(fw.code.toUpperCase(), fw.id);
      // Also map word tokens (e.g. "Business", "Process", "Fit")
      fw.name.split(/\s+/).forEach((w: string) => {
        if (w.length >= 3) fwLookup.set(w.toUpperCase(), fw.id);
      });
    });

    const rows: BulkModelRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const fields = parseCSVLine(lines[i]);
      if (fields.length < 1 || !fields[0]) continue;

      const name = fields[0] || '';
      const frameworkRaw = (fields[1] || '').trim();
      const appCodesRaw = (fields[2] || '').trim();
      const description = fields[3] || '';

      // Resolve framework ID
      const frameworkId = fwLookup.get(frameworkRaw.toUpperCase()) || frameworkRaw;

      // Parse semicolon-separated application codes
      const appCodes = appCodesRaw
        ? appCodesRaw.split(';').map(c => c.trim()).filter(c => c)
        : [];

      // Match codes to application IDs
      const matchedAppIds: string[] = [];
      const unmatchedCodes: string[] = [];
      appCodes.forEach(code => {
        const id = codeToId.get(code.toUpperCase());
        if (id) {
          matchedAppIds.push(id);
        } else {
          unmatchedCodes.push(code);
        }
      });

      rows.push({ name, frameworkId, applicationCodes: appCodes, description, matchedAppIds, unmatchedCodes });
    }

    setBulkRows(rows);
    setShowBulkPreview(true);
  };

  const executeBulkImport = async () => {
    setBulkImporting(true);
    let created = 0;
    let failed = 0;
    const errors: string[] = [];

    // Capture unmatched before clearing bulkRows
    const unmatchedByModel = bulkRows
      .filter(r => r.unmatchedCodes.length > 0)
      .map(r => ({ model: r.name, codes: [...r.unmatchedCodes] }));

    try {
      // Build a set of already-assigned app IDs for duplicate detection (single query)
      const assignedIds = await apiService.getAssignedApplicationIds();
      const assignedAppSet = new Set<string>(assignedIds);

      // Build a set of existing model names to prevent duplicates
      const existingModelNames = new Set(models.map(m => m.name.trim().toLowerCase()));

      for (const row of bulkRows) {
        try {
          // Skip if a model with the same name already exists
          if (existingModelNames.has(row.name.trim().toLowerCase())) {
            failed++;
            errors.push(`${row.name}: Model with this name already exists — skipped`);
            continue;
          }

          // Check for apps already assigned to other models
          const duplicates: string[] = [];
          const cleanAppIds: string[] = [];
          for (const appId of row.matchedAppIds) {
            if (assignedAppSet.has(appId)) {
              const appCode = applications.find(a => a.id === appId)?.applicationIdCode || appId;
              duplicates.push(appCode);
            } else {
              cleanAppIds.push(appId);
            }
          }

          if (duplicates.length > 0) {
            errors.push(`${row.name}: ${duplicates.length} app(s) already assigned — ${duplicates.join(', ')}`);
          }

          const newModel = await apiService.createModel({
            name: row.name,
            description: row.description,
            frameworkId: row.frameworkId,
            status: 'Created'
          });

          // Only assign apps that aren't already in other models
          if (cleanAppIds.length > 0) {
            await apiService.updateModelApplications(newModel.id, cleanAppIds);
          }

          // Track newly assigned apps and model name so subsequent rows detect them too
          cleanAppIds.forEach(id => assignedAppSet.add(id));
          existingModelNames.add(row.name.trim().toLowerCase());

          created++;
        } catch (error: any) {
          failed++;
          errors.push(`${row.name}: ${error.message || 'Unknown error'}`);
        }
      }
    } finally {
      setBulkImporting(false);
      setShowBulkPreview(false);
      setBulkRows([]);
      console.log('📊 Import complete:', { created, failed, unmatchedCount: unmatchedByModel.length, errors });
      setImportLog({ created, failed, unmatchedByModel, errors });
      setShowImportLog(true);
    }
  };

  // ========== Delete All Functions ==========

  const handleDeleteAll = async () => {
    if (deleteConfirmText !== 'DELETE') {
      alert('Please type "DELETE" to confirm deletion');
      return;
    }

    let deleted = 0;
    let skipped = 0;
    const skippedNames: string[] = [];
    const errors: string[] = [];

    for (const model of models) {
      // Skip models in execution (In-Process status means Q&A has started)
      if (model.status === 'In-Process') {
        skipped++;
        skippedNames.push(`${model.name} (In-Process)`);
        continue;
      }

      // Also check if model has any Q&A assessment data
      try {
        const modelApps = await apiService.getModelApplications(model.id);
        let hasQAData = false;
        for (const app of modelApps) {
          const response = await authFetch(`/api/assessments/qa/${app.id}`);
          if (response.ok) {
            const qaData = await response.json();
            if (Array.isArray(qaData) && qaData.length > 0) {
              hasQAData = true;
              break;
            }
          }
        }

        if (hasQAData) {
          skipped++;
          skippedNames.push(`${model.name} (Has Q&A data)`);
          continue;
        }
      } catch {
        // If we can't check, skip to be safe
      }

      try {
        await apiService.deleteModel(model.id);
        deleted++;
      } catch (error: any) {
        errors.push(`${model.name}: ${error.response?.data?.error || error.message}`);
      }
    }

    setShowDeleteAllModal(false);
    setDeleteConfirmText('');

    let message = `Delete All complete!\n\nDeleted: ${deleted}\nSkipped (in execution): ${skipped}`;
    if (skippedNames.length > 0) {
      message += `\n\nSkipped models:\n${skippedNames.slice(0, 10).join('\n')}`;
      if (skippedNames.length > 10) message += `\n...and ${skippedNames.length - 10} more`;
    }
    if (errors.length > 0) {
      message += `\n\nErrors:\n${errors.slice(0, 5).join('\n')}`;
    }

    alert(message);
    await refreshData();
  };

  // ========== Unassigned Applications Functions ==========

  const loadUnassignedApplications = async () => {
    try {
      // Two parallel calls instead of N+1 per model
      const [allApps, assignedIdsList] = await Promise.all([
        apiService.getApplications(),
        apiService.getAssignedApplicationIds()
      ]);
      const assignedIds = new Set<string>(assignedIdsList);

      const unassigned = allApps.filter((app: Application) => !assignedIds.has(app.id) && !app.isParent);
      setUnassignedApps(unassigned);
      setSelectedUnassignedApps(new Set());
      setAssignToModelId('');
      setUnassignedFilter('');
      setShowUnassigned(true);
    } catch (error) {
      console.error('Error loading unassigned applications:', error);
      alert('Failed to load unassigned applications.');
    }
  };

  const handleAssignToModel = async () => {
    if (!assignToModelId) {
      alert('Please select an APR Model to assign applications to.');
      return;
    }
    if (selectedUnassignedApps.size === 0) {
      alert('Please select at least one application.');
      return;
    }

    const targetModel = models.find(m => m.id === assignToModelId);
    if (targetModel && targetModel.status !== 'Created') {
      alert(`Cannot assign to "${targetModel.name}" — model status is "${targetModel.status}". Only models in "Created" status can receive new applications.`);
      return;
    }

    setAssigningApps(true);
    try {
      // Get current model applications
      const currentApps = await apiService.getModelApplications(assignToModelId);
      const currentIds = currentApps.map((a: Application) => a.id);

      // Merge with newly selected
      const mergedSet = new Set(currentIds);
      selectedUnassignedApps.forEach(id => mergedSet.add(id));
      const mergedIds: string[] = Array.from(mergedSet) as string[];

      await apiService.updateModelApplications(assignToModelId, mergedIds);

      alert(`Successfully assigned ${selectedUnassignedApps.size} application(s) to "${targetModel?.name}".`);
      setShowUnassigned(false);
      await refreshData();
    } catch (error: any) {
      alert(`Error assigning applications: ${error.message}`);
    } finally {
      setAssigningApps(false);
    }
  };

  const downloadModelData = async () => {
    try {
      const allApps = await apiService.getApplications();
      const appMap = new Map<string, any>();
      allApps.forEach((app: Application) => appMap.set(app.id, app));

      const headers = ['APR Model Name', 'Application ID', 'Application Name'];
      const csvRows = [headers.join(',')];

      for (const model of models) {
        const modelApps = await apiService.getModelApplications(model.id);
        if (modelApps.length === 0) {
          csvRows.push(`"${(model.name || '').replace(/"/g, '""')}","",""`);
        } else {
          for (const app of modelApps) {
            csvRows.push([
              `"${(model.name || '').replace(/"/g, '""')}"`,
              `"${(app.applicationIdCode || '').replace(/"/g, '""')}"`,
              `"${(app.name || '').replace(/"/g, '""')}"`
            ].join(','));
          }
        }
      }

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `apr_model_data_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading model data:', error);
      alert('Failed to download model data.');
    }
  };

  const handleEdit = async (model: APRModel) => {
    setIsViewOnly(false);
    setEditingModel(model);
    setFormData({
      name: model.name,
      description: model.description || '',
      frameworkId: model.frameworkId || ''
    });

    try {
      // Load everything in parallel — 4 calls, no N+1
      const [frameworks, allApps, modelApps, assignedIds] = await Promise.all([
        apiService.getActiveFrameworks(),
        apiService.getApplications(),
        apiService.getModelApplications(model.id),
        apiService.getAssignedApplicationIds()
      ]);

      setActiveFrameworks(frameworks);
      setAllApplications(allApps);
      setAssignedApplicationIds(new Set<string>(assignedIds));

      const selectedIds = modelApps.map((app: Application) => app.id);
      setSelectedApplications(selectedIds);

      if (model.status === 'Created') {
        setAvailableApplications(allApps);
      } else {
        setAvailableApplications(modelApps);
      }
    } catch (error) {
      console.error('Error loading edit data:', error);
      setSelectedApplications([]);
      setAvailableApplications([]);
    }

    setShowForm(true);
  };

  const handleView = async (model: APRModel) => {
    setIsViewOnly(true);
    setEditingModel(model);
    setFormData({
      name: model.name,
      description: model.description || '',
      frameworkId: model.frameworkId || ''
    });

    try {
      const [frameworks, modelApps] = await Promise.all([
        apiService.getActiveFrameworks(),
        apiService.getModelApplications(model.id)
      ]);
      setActiveFrameworks(frameworks);
      const selectedIds = modelApps.map((app: Application) => app.id);
      setSelectedApplications(selectedIds);
      setAvailableApplications(modelApps);
    } catch (error) {
      console.error('Error loading view data:', error);
      setSelectedApplications([]);
      setAvailableApplications([]);
    }

    setShowForm(true);
  };



  const handleDelete = async (model: APRModel) => {
    if (window.confirm(`Are you sure you want to delete APR Model Definition "${model.name}"?`)) {
      try {
        await apiService.deleteModel(model.id);
        alert('APR Model Definition deleted successfully!');
        await refreshData();
      } catch (error: any) {
        // Handle dependency error with detailed information
        if (error.response?.status === 400) {
          const errorData = error.response.data;
          if (errorData.details) {
            let message = `❌ Cannot Delete APR Model Definition\n\n${errorData.error}\n\n`;
            
            if (errorData.details.reason === 'Model has associated APR runs') {
              message += `This model has ${errorData.details.runCount} APR run(s).\n\nTo delete this model:\n1. Delete all associated APR runs first\n2. Then try deleting the model again\n\n${errorData.details.suggestion}`;
            } else if (errorData.details.reason === 'Model has assigned applications') {
              message += `This model has ${errorData.details.applicationCount} assigned application(s).\n\nTo delete this model:\n1. Go to Application Master Data\n2. Unassign all applications from this model\n3. Then try deleting the model again\n\n${errorData.details.suggestion}`;
            }
            
            alert(message);
          } else {
            alert(`❌ Cannot Delete APR Model Definition\n\n${errorData.error}`);
          }
        } else {
          const errorMessage = error.response?.data?.error || error.message;
          alert(`Error deleting APR Model Definition: ${errorMessage}`);
        }
      }
    }
  };

  const getStatusChip = (status: string) => {
    const chipStyles = {
      'Created': 'status-chip status-chip--created',
      'Scheduled': 'status-chip status-chip--scheduled',
      'In-Process': 'status-chip status-chip--in-process',
      'Completed': 'status-chip status-chip--completed'
    }[status] || 'status-chip';
    
    const icons = {
      'Created': '📝',
      'Scheduled': '📅',
      'In-Process': '⚡',
      'Completed': '✅'
    }[status] || '📝';
    
    return (
      <span className={chipStyles}>
        <span className="status-chip__icon">{icons}</span>
        <span className="status-chip__text">{status}</span>
      </span>
    );
  };

  // Application search with debounce
  React.useEffect(() => {
    if (!appSearch.trim()) {
      setAppSearchModelIds(null);
      return;
    }
    const timer = setTimeout(async () => {
      setAppSearchLoading(true);
      try {
        const matchingModelIds = await apiService.searchModelsByApplication(appSearch.trim());
        setAppSearchModelIds(new Set(matchingModelIds));
      } catch {
        setAppSearchModelIds(new Set());
      }
      setAppSearchLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [appSearch]);

  const filteredModels = (() => {
    let result = models;
    if (modelSearch.trim()) {
      const search = modelSearch.toLowerCase();
      result = result.filter(m =>
        (m.name || '').toLowerCase().includes(search) ||
        (m.description || '').toLowerCase().includes(search)
      );
    }
    if (appSearchModelIds !== null) {
      result = result.filter(m => appSearchModelIds.has(m.id));
    }
    return result;
  })();

  return (
    <div className="page">
      {/* Import Results Log Modal — rendered at top level for z-index priority */}
      {showImportLog && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <h2 style={{ margin: '0 0 16px 0', color: '#1976d2' }}>Import Results</h2>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
              <div style={{ padding: '12px 20px', backgroundColor: '#d1fae5', borderRadius: '8px', flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>{importLog.created}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Models Created</div>
              </div>
              {importLog.failed > 0 && (
                <div style={{ padding: '12px 20px', backgroundColor: '#fee2e2', borderRadius: '8px', flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#dc2626' }}>{importLog.failed}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Failed</div>
                </div>
              )}
              <div style={{ padding: '12px 20px', backgroundColor: importLog.unmatchedByModel.length > 0 ? '#fef3c7' : '#f0fdf4', borderRadius: '8px', flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: importLog.unmatchedByModel.length > 0 ? '#f59e0b' : '#10b981' }}>
                  {importLog.unmatchedByModel.reduce((s, m) => s + m.codes.length, 0)}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Apps Not Found</div>
              </div>
            </div>

            {importLog.unmatchedByModel.length > 0 && (
              <>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#f59e0b' }}>
                  Application IDs Not Found in Application Master
                </h3>
                <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Model Name</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Unmatched Application IDs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importLog.unmatchedByModel.map((entry, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '6px 12px', verticalAlign: 'top', fontWeight: 500 }}>{entry.model}</td>
                          <td style={{ padding: '6px 12px', color: '#dc2626', fontSize: '12px', wordBreak: 'break-all' }}>
                            {entry.codes.join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {importLog.errors.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#dc2626' }}>Errors</h3>
                <div style={{ backgroundColor: '#fef2f2', borderRadius: '8px', padding: '12px', fontSize: '12px', maxHeight: '150px', overflow: 'auto' }}>
                  {importLog.errors.map((err, i) => (
                    <div key={i} style={{ marginBottom: '4px' }}>{err}</div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button className="btn btn-primary" onClick={() => { setShowImportLog(false); refreshData(); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title" style={{ fontSize: '28px' }}>APR Model Definition</h1>
            <p className="page-header__subtitle" style={{ fontSize: '16px' }}>
              Define APR Models to group applications by business process, domain, or function, creating logical sets that will be used as the basis for rationalization.
            </p>
          </div>
          <div className="page-header__actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              className="btn btn-primary"
              style={{ fontSize: '14px', padding: '8px 16px', fontWeight: '500' }}
              onClick={async () => {
                // Reset form state for new model creation
                setEditingModel(null);
                setFormData({
                  name: '',
                  description: '',
                  frameworkId: ''
                });
                setSelectedApplications([]);
                setApplicationFilter('');
                setSelectedAppFilter('');
                setAvailableAppFilter('');

                // Load active frameworks and unassigned applications for new model creation
                try {
                  const [applicationsData, modelsData, frameworksData] = await Promise.all([
                    apiService.getApplications(),
                    apiService.getModels(),
                    apiService.getActiveFrameworks()
                  ]);

                  console.log('Loaded frameworks:', frameworksData);
                  console.log('Loaded applications:', applicationsData?.length);

                  // Set active frameworks
                  setActiveFrameworks(frameworksData);

                  // Update assigned application IDs
                  const assignedAppIds = await loadAssignedApplicationIds(modelsData);

                  // Filter to get only unassigned applications
                  const unassignedApps = applicationsData.filter((app: Application) => !assignedAppIds.has(app.id));
                  console.log('Unassigned apps:', unassignedApps?.length);

                  setAvailableApplications(unassignedApps);
                  setAllApplications(applicationsData);

                  // Show form AFTER all data is loaded
                  setShowForm(true);
                } catch (error) {
                  console.error('Error loading data:', error);
                  alert('Error loading data. Please try again.');
                }
              }}
            >
              + Add APR Model
            </button>
            <button
              className="btn btn-outline-info"
              onClick={downloadTemplate}
              title="Download CSV template for creating APR models"
              style={{ fontSize: '14px', padding: '8px 16px', fontWeight: '500' }}
            >
              📋 Download Template
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => fileInputRef.current?.click()}
              title="Upload CSV file to create APR models"
              style={{ fontSize: '14px', padding: '8px 16px', fontWeight: '500' }}
            >
              📤 Create from File
            </button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            <button
              className="btn btn-outline-secondary"
              onClick={loadUnassignedApplications}
              title="View and assign unassigned applications to APR models"
              style={{ fontSize: '14px', padding: '8px 16px', fontWeight: '500' }}
            >
              📋 Unassigned Applications
            </button>
            <button
              className="btn btn-outline-success"
              onClick={downloadModelData}
              style={{ fontSize: '14px', padding: '8px 16px', fontWeight: '500', border: '2px solid #28a745' }}
              title="Download APR model data with applications as CSV"
            >
              📥 Download Data
            </button>
            {currentUser?.role === 'Administrator' && (
              <button
                className="btn btn-danger"
                onClick={() => { setShowDeleteAllModal(true); setDeleteConfirmText(''); }}
                style={{ fontSize: '14px', padding: '8px 16px', fontWeight: '500' }}
              >
                🗑️ Delete All
              </button>
            )}

            {/* Spacer to push toggle to far right */}
            <div style={{ flex: 1 }} />

            {/* Grid Scale Slider - only visible in grid mode */}
            {viewMode === 'grid' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>Size:</span>
                <input
                  type="range"
                  min="250"
                  max="500"
                  value={gridScale}
                  onChange={(e) => setGridScale(Number(e.target.value))}
                  style={{ width: '100px', cursor: 'pointer' }}
                  title={`Grid card width: ${gridScale}px`}
                />
              </div>
            )}

            {/* View Toggle Buttons */}
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: '8px 10px',
                  border: viewMode === 'list' ? '2px solid #1976d2' : '1px solid #ddd',
                  backgroundColor: viewMode === 'list' ? '#e3f2fd' : 'white',
                  color: viewMode === 'list' ? '#1976d2' : '#666',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                title="List View"
              >
                ☰
              </button>
              <button
                onClick={() => setViewMode('grid')}
                style={{
                  padding: '8px 10px',
                  border: viewMode === 'grid' ? '2px solid #1976d2' : '1px solid #ddd',
                  backgroundColor: viewMode === 'grid' ? '#e3f2fd' : 'white',
                  color: viewMode === 'grid' ? '#1976d2' : '#666',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                title="Grid View"
              >
                ⊞
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Model Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '700px',
              maxWidth: '900px',
              maxHeight: '95vh',
              overflowY: 'auto',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                {editingModel
                  ? (isViewOnly || editingModel.status !== 'Created'
                    ? 'View APR Model Definition'
                    : 'Edit APR Model Definition')
                  : 'Create New APR Model Definition'}
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {(isViewOnly || editingModel?.status !== 'Created') && editingModel
                  ? 'Model details (read-only)'
                  : (editingModel
                    ? 'Update APR model definition and application assignments'
                    : 'Create a new APR model to group applications for rationalization')
                }
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="name" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Model Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={!!(editingModel && (isViewOnly || editingModel.status !== 'Created'))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: (editingModel && (isViewOnly || editingModel.status !== 'Created')) ? '#f5f5f5' : 'white',
                    cursor: (editingModel && (isViewOnly || editingModel.status !== 'Created')) ? 'not-allowed' : 'text'
                  }}
                  placeholder="Enter a descriptive name for this APR model..."
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="frameworkId" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Framework *
                </label>
                <select
                  id="frameworkId"
                  name="frameworkId"
                  value={formData.frameworkId}
                  onChange={handleInputChange}
                  required
                  disabled={!!(editingModel && (isViewOnly || editingModel.status !== 'Created'))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: (editingModel && (isViewOnly || editingModel.status !== 'Created')) ? '#f5f5f5' : 'white',
                    cursor: (editingModel && (isViewOnly || editingModel.status !== 'Created')) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">-- Select a Framework --</option>
                  {activeFrameworks.map(fw => (
                    <option key={fw.id} value={fw.id}>
                      {fw.code} - {fw.name}
                    </option>
                  ))}
                </select>
                <small style={{
                  display: 'block',
                  marginTop: '6px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  Select the rationalization framework to use for scoring applications in this model.
                </small>
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Status
                </label>
                <div>
                  {editingModel ? (
                    getStatusChip(editingModel.status)
                  ) : (
                    <span className="status-chip status-chip--created">
                      <span className="status-chip__icon">📝</span>
                      <span className="status-chip__text">Created</span>
                    </span>
                  )}
                </div>
              </div>
                {/* Application Selection */}
                {(!editingModel || (editingModel && !isViewOnly && editingModel.status === 'Created')) ? (
                  /* New Model Creation & Edit Model with Created Status - Two Column Layout */
                  <div className="applications-two-column">
                    {/* Selected Applications Column */}
                    <div className="applications-column">
                      <label className="material-label">
                        <span className="label-icon">✅</span>
                        Selected Applications
                      </label>
                      <div className="application-selection material-surface">
                        <div className="application-filter">
                          <input
                            type="text"
                            placeholder="🔍 Search selected applications..."
                            value={selectedAppFilter}
                            onChange={(e) => setSelectedAppFilter(e.target.value)}
                            className="filter-input material-input"
                          />
                        </div>
                        <div className="application-list material-scroll selected-apps">
                          {selectedApplications.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                              <span style={{ fontSize: '24px' }}>📭</span>
                              <p style={{ margin: '8px 0 0 0' }}>No applications selected yet. Add applications from the Available panel.</p>
                            </div>
                          ) : (
                            (editingModel ? availableApplications : allApplications)
                              .filter(app => selectedApplications.includes(app.id))
                              .filter(app => {
                                if (!selectedAppFilter) return true;
                                const filterLower = selectedAppFilter.toLowerCase();
                                return (
                                  app.name.toLowerCase().includes(filterLower) ||
                                  (app.applicationIdCode?.toLowerCase().includes(filterLower)) ||
                                  (app.description?.toLowerCase().includes(filterLower))
                                );
                              })
                              .sort((a, b) => {
                                const aId = a.applicationIdCode || a.name;
                                const bId = b.applicationIdCode || b.name;
                                return aId.localeCompare(bId, undefined, { numeric: true });
                              })
                              .map(app => (
                              <div key={app.id} className="app-item material-app-card">
                                <div className="app-content">
                                  <div className="app-primary">
                                    {app.applicationIdCode && (
                                      <span className="app-id">{app.applicationIdCode}</span>
                                    )}
                                    <strong className="app-name">{app.name}</strong>
                                  </div>
                                  {app.description && (
                                    <div className="app-description">{app.description}</div>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="app-action-btn remove-btn"
                                  onClick={() => setSelectedApplications(prev => prev.filter(id => id !== app.id))}
                                  title="Remove from model"
                                >
                                  ➖
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="selection-summary material-chip">
                          <span className="summary-icon">✅</span>
                          <span className="summary-text">
                            {selectedApplications.length} selected
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Available Applications Column */}
                    <div className="applications-column">
                      <label className="material-label">
                        <span className="label-icon">📱</span>
                        Available Applications (Unassigned child apps only)
                      </label>
                      <div className="application-selection material-surface">
                        <div className="application-filter">
                          <input
                            type="text"
                            placeholder="🔍 Search available applications..."
                            value={availableAppFilter}
                            onChange={(e) => setAvailableAppFilter(e.target.value)}
                            className="filter-input material-input"
                          />
                          <div className="bulk-actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                const filteredApps = (editingModel ? allApplications : availableApplications)
                                  .filter(app => !app.isParent)
                                  .filter(app => !selectedApplications.includes(app.id))
                                  .filter(app => editingModel ? !assignedApplicationIds.has(app.id) : true)
                                  .filter(app => {
                                    if (!availableAppFilter) return true;
                                    const filterLower = availableAppFilter.toLowerCase();
                                    return (
                                      app.name.toLowerCase().includes(filterLower) ||
                                      (app.applicationIdCode?.toLowerCase().includes(filterLower)) ||
                                      (app.description?.toLowerCase().includes(filterLower))
                                    );
                                  });
                                const filteredIds = filteredApps.map(app => app.id);
                                setSelectedApplications(prev => [...prev, ...filteredIds]);
                              }}
                              title="Select all filtered available applications"
                            >
                              ✅ Select All
                            </button>
                          </div>
                        </div>
                        <div className="application-list material-scroll available-apps">
                          {(editingModel ? allApplications : availableApplications)
                            .filter(app => !app.isParent)
                            .filter(app => !selectedApplications.includes(app.id))
                            .filter(app => editingModel ? !assignedApplicationIds.has(app.id) : true)
                            .filter(app => {
                              if (!availableAppFilter) return true;
                              const filterLower = availableAppFilter.toLowerCase();
                              return (
                                app.name.toLowerCase().includes(filterLower) ||
                                (app.applicationIdCode?.toLowerCase().includes(filterLower)) ||
                                (app.description?.toLowerCase().includes(filterLower))
                              );
                            })
                            .sort((a, b) => {
                              const aId = a.applicationIdCode || a.name;
                              const bId = b.applicationIdCode || b.name;
                              return aId.localeCompare(bId, undefined, { numeric: true });
                            })
                            .map(app => (
                            <div key={app.id} className="app-item material-app-card">
                              <div className="app-content">
                                <div className="app-primary">
                                  {app.applicationIdCode && (
                                    <span className="app-id">{app.applicationIdCode}</span>
                                  )}
                                  <strong className="app-name">{app.name}</strong>
                                </div>
                                {app.description && (
                                  <div className="app-description">{app.description}</div>
                                )}
                              </div>
                              <button
                                type="button"
                                className="app-action-btn add-btn"
                                onClick={() => setSelectedApplications(prev => [...prev, app.id])}
                                title="Add to model"
                              >
                                ➕
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="selection-summary material-chip">
                          <span className="summary-icon">📋</span>
                          <span className="summary-text">
                            {(editingModel ? allApplications : availableApplications).filter(app => !app.isParent && !selectedApplications.includes(app.id) && (editingModel ? !assignedApplicationIds.has(app.id) : true)).length} available
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Read-only view for non-Created status */
                  <div className="form-row">
                    <div className="form-group">
                      <label className="material-label">
                        <span className="label-icon">📱</span>
                        Assigned Applications (Read-only)
                      </label>
                      <div className="application-selection material-surface read-only">
                        <div className="application-list material-scroll">
                          {availableApplications
                            .sort((a, b) => {
                              const aId = a.applicationIdCode || a.name;
                              const bId = b.applicationIdCode || b.name;
                              return aId.localeCompare(bId, undefined, { numeric: true });
                            })
                            .map(app => (
                            <div key={app.id} className="app-item material-app-card selected read-only">
                              <div className="app-content">
                                <div className="app-primary">
                                  {app.applicationIdCode && (
                                    <span className="app-id">{app.applicationIdCode}</span>
                                  )}
                                  <strong className="app-name">{app.name}</strong>
                                </div>
                                {app.description && (
                                  <div className="app-description">{app.description}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="selection-summary material-chip">
                          <span className="summary-icon">🔒</span>
                          <span className="summary-text">
                            {availableApplications.length} application{availableApplications.length !== 1 ? 's' : ''} (Cannot modify - Model is {editingModel?.status})
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label htmlFor="description" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    disabled={!!(editingModel && (isViewOnly || editingModel.status !== 'Created'))}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      backgroundColor: (editingModel && (isViewOnly || editingModel.status !== 'Created')) ? '#f5f5f5' : 'white',
                      cursor: (editingModel && (isViewOnly || editingModel.status !== 'Created')) ? 'not-allowed' : 'text'
                    }}
                    placeholder="Describe the purpose and scope of this APR model..."
                  />
                  <small style={{
                    display: 'block',
                    marginTop: '6px',
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    This description helps users understand what this model covers during application rationalization.
                  </small>
                </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#666',
                    backgroundColor: 'transparent',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {(isViewOnly || editingModel?.status !== 'Created') ? 'Close' : 'Cancel'}
                </button>
                {(!editingModel || (!isViewOnly && editingModel?.status === 'Created')) && (
                  <button
                    type="submit"
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'white',
                      backgroundColor: '#1976d2',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
                  >
                    {editingModel ? 'Update Model Definition' : 'Create Model Definition'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search Bars */}
      <div style={{ padding: '0 24px', marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by model name or description..."
          value={modelSearch}
          onChange={(e) => setModelSearch(e.target.value)}
          style={{
            width: '100%', maxWidth: '350px', padding: '10px 14px',
            border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', outline: 'none'
          }}
        />
        <input
          type="text"
          placeholder="Search by application ID or name..."
          value={appSearch}
          onChange={(e) => setAppSearch(e.target.value)}
          style={{
            width: '100%', maxWidth: '350px', padding: '10px 14px',
            border: appSearchModelIds !== null ? '2px solid #1976d2' : '1px solid #ddd',
            borderRadius: '8px', fontSize: '14px', outline: 'none'
          }}
        />
        {appSearch && appSearchLoading && (
          <span style={{ fontSize: '13px', color: '#999' }}>Searching...</span>
        )}
        {(modelSearch || appSearchModelIds !== null) && (
          <span style={{ fontSize: '13px', color: '#666' }}>
            {filteredModels.length} of {models.length} models
            {appSearchModelIds !== null && !appSearchLoading && (
              <button
                onClick={() => { setAppSearch(''); setAppSearchModelIds(null); }}
                style={{
                  marginLeft: '8px', padding: '2px 8px', fontSize: '12px',
                  border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer',
                  backgroundColor: '#f5f5f5'
                }}
              >
                Clear app filter
              </button>
            )}
          </span>
        )}
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">
        {filteredModels.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🎯</div>
            <h3>{modelSearch ? 'No Matching APR Models' : 'No APR Model Definitions'}</h3>
            <p>{modelSearch ? `No models match "${modelSearch}". Try a different search term.` : 'Click the "Add APR Model" button above to create your first APR model definition.'}</p>
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div className="md-table-container">
            <table className="md-table" style={{ fontSize: '14px', tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '5%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>#</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Model Name</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Framework</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Status</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Applications</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredModels.map((model, index) => (
                  <tr key={model.id}>
                    <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                      {index + 1}
                    </td>
                    <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', wordWrap: 'break-word' }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{model.name}</div>
                        {model.description && (
                          <div style={{ fontSize: '13px', color: '#666' }}>{model.description}</div>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>
                      {model.frameworkName || <span style={{ color: '#999', fontStyle: 'italic' }}>Not assigned</span>}
                    </td>
                    <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 8px', borderRadius: '12px', fontSize: '13px', fontWeight: '500', backgroundColor: model.status === 'Created' ? '#e8f5e8' : model.status === 'Scheduled' ? '#e3f2fd' : model.status === 'In-Process' ? '#fff3e0' : '#f3e5f5', color: model.status === 'Created' ? '#2e7d32' : model.status === 'Scheduled' ? '#1565c0' : model.status === 'In-Process' ? '#ef6c00' : '#7b1fa2' }}>
                        <span>
                          {model.status === 'Created' && '📝'}
                          {model.status === 'Scheduled' && '📅'}
                          {model.status === 'In-Process' && '⚡'}
                          {model.status === 'Completed' && '✅'}
                        </span>
                        <span>{model.status}</span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>{model.applicationCount || 0}</span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        {/* View button - always shown */}
                        <button
                          onClick={() => handleView(model)}
                          title="View model"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          👁️
                        </button>
                        {model.status === 'Created' && (
                          <>
                            <button
                              onClick={() => handleEdit(model)}
                              title="Edit model"
                              style={{
                                padding: '4px 6px',
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                border: '1px solid #1976d2',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                minWidth: 'auto'
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDelete(model)}
                              title="Delete model"
                              style={{
                                padding: '4px 6px',
                                backgroundColor: 'transparent',
                                color: '#d32f2f',
                                border: '1px solid #d32f2f',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                minWidth: 'auto'
                              }}
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Grid View */
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${gridScale}px, 1fr))`,
            gap: '20px',
            padding: '20px'
          }}>
            {filteredModels.map((model) => {
              const statusBorderColor = model.status === 'Created' ? '#2e7d32' : model.status === 'Scheduled' ? '#1565c0' : model.status === 'In-Process' ? '#ef6c00' : '#7b1fa2';
              return (<div
                key={model.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: `2px solid ${statusBorderColor}`,
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  transition: 'box-shadow 0.2s, transform 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Header with Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', height: '48px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '24px', flexShrink: 0 }}>🎯</span>
                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#333', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }} title={model.name}>{model.name}</h3>
                  </div>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    flexShrink: 0,
                    backgroundColor: model.status === 'Created' ? '#e8f5e8' : model.status === 'Scheduled' ? '#e3f2fd' : model.status === 'In-Process' ? '#fff3e0' : '#f3e5f5',
                    color: model.status === 'Created' ? '#2e7d32' : model.status === 'Scheduled' ? '#1565c0' : model.status === 'In-Process' ? '#ef6c00' : '#7b1fa2'
                  }}>
                    <span>
                      {model.status === 'Created' && '📝'}
                      {model.status === 'Scheduled' && '📅'}
                      {model.status === 'In-Process' && '⚡'}
                      {model.status === 'Completed' && '✅'}
                    </span>
                    <span>{model.status}</span>
                  </div>
                </div>

                {/* Description */}
                <p style={{
                  margin: 0,
                  fontSize: '12px',
                  color: '#666',
                  lineHeight: '1.4',
                  height: '34px',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical' as any
                }} title={model.description || ''}>
                  {model.description || 'No description provided'}
                </p>

                {/* Info Row */}
                <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#666' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>📚</span>
                    <span>{model.frameworkName || 'No framework'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>📱</span>
                    <span>{model.applicationCount || 0} apps</span>
                  </div>
                </div>

                {/* Progress Bar - Segmented Style */}
                {(() => {
                  const totalApps = model.applicationCount || 0;
                  const completedApps = model.completedApplications || 0;
                  const isInProcess = model.status === 'In-Process';

                  // Color gradient from red to dark green based on progress
                  const getProgressColor = (completedIndex: number, total: number) => {
                    if (total === 0) return '#bdbdbd';
                    const percent = ((completedIndex + 1) / total) * 100;
                    if (percent <= 25) return '#dc3545'; // Red
                    if (percent <= 50) return '#fd7e14'; // Orange
                    if (percent <= 75) return '#ffc107'; // Yellow
                    if (percent <= 90) return '#66bb6a'; // Light green
                    return '#2e7d32'; // Dark green
                  };

                  // Calculate number of segments (max 20 for display, scale if more)
                  const maxSegments = 20;
                  const displaySegments = Math.min(totalApps, maxSegments);
                  const scaleFactor = totalApps > maxSegments ? totalApps / maxSegments : 1;
                  const filledSegments = Math.round(completedApps / scaleFactor);

                  return (
                    <div style={{ width: '95%', margin: '8px auto' }}>
                      {/* Progress label */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '11px',
                        color: isInProcess ? '#333' : '#999',
                        marginBottom: '4px'
                      }}>
                        <span>Q&A Progress</span>
                        <span style={{ fontWeight: '500' }}>{completedApps} / {totalApps}</span>
                      </div>
                      {/* Segmented progress bar */}
                      <div style={{
                        display: 'flex',
                        gap: '2px',
                        width: '100%',
                        height: '12px'
                      }}>
                        {Array.from({ length: displaySegments || 1 }).map((_, index) => {
                          const isFilled = index < filledSegments;
                          const segmentColor = isFilled && isInProcess
                            ? getProgressColor(index, displaySegments)
                            : isFilled
                              ? '#bdbdbd'
                              : '#e8e8e8';
                          return (
                            <div
                              key={index}
                              style={{
                                flex: 1,
                                height: '100%',
                                backgroundColor: segmentColor,
                                borderRadius: '2px',
                                transition: 'background-color 0.3s ease'
                              }}
                            />
                          );
                        })}
                      </div>
                      {/* Scale markers */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '10px',
                        color: isInProcess ? '#666' : '#999',
                        marginTop: '2px'
                      }}>
                        <span>0</span>
                        <span>{totalApps}</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Divider + Actions pinned to bottom */}
                <div style={{ marginTop: 'auto' }}>
                  <div style={{ borderTop: '1px solid #eee', margin: '4px 0 8px 0' }} />
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                    <button
                      onClick={() => handleView(model)}
                      title="View model"
                      style={{
                        padding: '6px 10px',
                        backgroundColor: 'transparent',
                        color: '#1976d2',
                        border: '1px solid #1976d2',
                        borderRadius: '6px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <span>👁️</span>
                      <span>View</span>
                    </button>
                    {model.status === 'Created' && (
                      <>
                        <button
                          onClick={() => handleEdit(model)}
                          title="Edit model"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <span>✏️</span>
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDelete(model)}
                          title="Delete model"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: 'transparent',
                            color: '#d32f2f',
                            border: '1px solid #d32f2f',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <span>🗑️</span>
                          <span>Delete</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>);
            })}
          </div>
        )}
      </div>

      {/* Delete All Confirmation Modal */}
      {showDeleteAllModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '32px',
            maxWidth: '500px', width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <h2 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>⚠️ Delete All APR Models</h2>
            <p style={{ color: '#666', marginBottom: '8px' }}>
              This will delete all APR models that are <strong>not in execution</strong>.
            </p>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
              Models with status "In-Process" or with Q&A assessment data will be skipped and preserved.
            </p>
            <p style={{ marginBottom: '12px', fontWeight: 600 }}>
              Type <span style={{ color: '#dc2626', fontFamily: 'monospace' }}>DELETE</span> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder='Type "DELETE" to confirm'
              style={{
                width: '100%', padding: '10px 12px', fontSize: '16px',
                border: '2px solid #e0e0e0', borderRadius: '8px',
                marginBottom: '20px', boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowDeleteAllModal(false); setDeleteConfirmText(''); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteAll}
                disabled={deleteConfirmText !== 'DELETE'}
              >
                🗑️ Delete All Models
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unassigned Applications Modal */}
      {showUnassigned && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '950px', width: '95%', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <h2 style={{ margin: '0 0 16px 0', color: '#1976d2' }}>
              Unassigned Applications ({unassignedApps.length})
            </h2>

            {/* Controls row */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Filter by name or ID..."
                value={unassignedFilter}
                onChange={(e) => setUnassignedFilter(e.target.value)}
                style={{ flex: 1, minWidth: '200px', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
              />
              <select
                value={assignToModelId}
                onChange={(e) => setAssignToModelId(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', minWidth: '250px' }}
              >
                <option value="">Select APR Model to assign to...</option>
                {models.filter(m => m.status === 'Created').map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              <button
                className="btn btn-primary"
                onClick={handleAssignToModel}
                disabled={assigningApps || selectedUnassignedApps.size === 0 || !assignToModelId}
                style={{ whiteSpace: 'nowrap' }}
              >
                {assigningApps ? 'Assigning...' : `Assign ${selectedUnassignedApps.size} App(s)`}
              </button>
            </div>

            {/* Select all */}
            {(() => {
              const filtered = unassignedApps.filter(app => {
                if (!unassignedFilter) return true;
                const f = unassignedFilter.toLowerCase();
                return (app.applicationIdCode || '').toLowerCase().includes(f) ||
                       (app.name || '').toLowerCase().includes(f) ||
                       (app.businessFunctionDomain || '').toLowerCase().includes(f);
              });
              return (
                <>
                  <div style={{ marginBottom: '8px', fontSize: '13px', color: '#666' }}>
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={filtered.length > 0 && filtered.every(a => selectedUnassignedApps.has(a.id))}
                        onChange={(e) => {
                          const newSet = new Set(selectedUnassignedApps);
                          if (e.target.checked) {
                            filtered.forEach(a => newSet.add(a.id));
                          } else {
                            filtered.forEach(a => newSet.delete(a.id));
                          }
                          setSelectedUnassignedApps(newSet);
                        }}
                        style={{ marginRight: '6px' }}
                      />
                      Select all ({filtered.length} shown)
                    </label>
                    {selectedUnassignedApps.size > 0 && (
                      <span style={{ marginLeft: '16px', color: '#1976d2', fontWeight: 600 }}>
                        {selectedUnassignedApps.size} selected
                      </span>
                    )}
                  </div>

                  {/* Table */}
                  <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
                          <th style={{ padding: '8px', width: '40px', borderBottom: '2px solid #e0e0e0' }}></th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>App ID</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Application Name</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Business Domain</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Technology</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Owner</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.length === 0 ? (
                          <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
                            {unassignedApps.length === 0 ? 'All applications are assigned to models.' : 'No applications match filter.'}
                          </td></tr>
                        ) : filtered.map(app => (
                          <tr
                            key={app.id}
                            style={{
                              borderBottom: '1px solid #eee',
                              backgroundColor: selectedUnassignedApps.has(app.id) ? '#e3f2fd' : undefined,
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              const newSet = new Set(selectedUnassignedApps);
                              if (newSet.has(app.id)) newSet.delete(app.id); else newSet.add(app.id);
                              setSelectedUnassignedApps(newSet);
                            }}
                          >
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <input type="checkbox" checked={selectedUnassignedApps.has(app.id)} readOnly />
                            </td>
                            <td style={{ padding: '6px 12px', fontWeight: 500 }}>{app.applicationIdCode}</td>
                            <td style={{ padding: '6px 12px' }}>{app.name}</td>
                            <td style={{ padding: '6px 12px', color: '#666' }}>{app.businessFunctionDomain || '—'}</td>
                            <td style={{ padding: '6px 12px', color: '#666' }}>{app.technologyStack || '—'}</td>
                            <td style={{ padding: '6px 12px', color: '#666' }}>{app.applicationOwner || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button className="btn btn-secondary" onClick={() => setShowUnassigned(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Preview Modal */}
      {showBulkPreview && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '900px', width: '90%', maxHeight: '80vh', overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <h2 style={{ margin: '0 0 16px 0', color: '#1976d2' }}>Import Preview — {bulkRows.length} Models</h2>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
              Review the models below before importing. Application codes are matched against existing applications.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>#</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Model Name</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e0e0e0' }}>Framework</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e0e0e0' }}>Apps Matched</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e0e0e0' }}>Not Found</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkRows.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '6px 12px', color: '#999' }}>{i + 1}</td>
                      <td style={{ padding: '6px 12px' }}>
                        <div style={{ fontWeight: 500 }}>{row.name}</div>
                        {row.description && (
                          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px', maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.description}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '6px 12px', textAlign: 'center' }}>{row.frameworkId}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'center', color: '#10b981', fontWeight: 600 }}>
                        {row.matchedAppIds.length}
                      </td>
                      <td style={{ padding: '6px 12px', textAlign: 'center', color: row.unmatchedCodes.length > 0 ? '#f59e0b' : '#999' }}>
                        {row.unmatchedCodes.length > 0 ? (
                          <span title={row.unmatchedCodes.join(', ')} style={{ cursor: 'help' }}>
                            {row.unmatchedCodes.length}
                          </span>
                        ) : '0'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '13px' }}>
              <strong>Summary:</strong>{' '}
              {bulkRows.length} models, {' '}
              {bulkRows.reduce((s, r) => s + r.matchedAppIds.length, 0)} applications matched, {' '}
              {bulkRows.reduce((s, r) => s + r.unmatchedCodes.length, 0)} not found
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowBulkPreview(false); setBulkRows([]); }}
                disabled={bulkImporting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={executeBulkImport}
                disabled={bulkImporting || bulkRows.length === 0}
              >
                {bulkImporting ? 'Importing...' : `Import ${bulkRows.length} Models`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APRModelPage;
